import React, { memo, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  SwipeableDrawer,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Sort as SortIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SORTABLE_COLUMN_SKIP = new Set(['_select', 'reserveAction']);

/**
 * Mobile sort bar + bottom sheet for ResponsiveTable card views.
 *
 * Props:
 *   columns         – table column defs ({ id, label, sortable? })
 *   sortBy          – active sort field id
 *   sortOrder       – 'asc' | 'desc'
 *   onSort          – (columnId, direction?) => void; direction optional
 *   defaultSortBy   – optional default field for reset chip
 *   defaultSortOrder – optional default direction (default 'desc')
 *   onResetSort     – optional () => void when reset chip is cleared
 */
const MobileTableSort = memo(({
  columns,
  sortBy,
  sortOrder,
  onSort,
  defaultSortBy,
  defaultSortOrder = 'desc',
  onResetSort,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const sortableColumns = useMemo(
    () => columns.filter((column) => column.sortable && !SORTABLE_COLUMN_SKIP.has(column.id)),
    [columns],
  );

  const activeColumn = useMemo(
    () => sortableColumns.find((column) => column.id === sortBy),
    [sortableColumns, sortBy],
  );

  const isNonDefault = defaultSortBy != null && (
    sortBy !== defaultSortBy || sortOrder !== defaultSortOrder
  );

  const handleColumnPress = useCallback((columnId) => {
    onSort?.(columnId);
    setOpen(false);
  }, [onSort]);

  const handleDirectionPress = useCallback((columnId, direction, event) => {
    event.stopPropagation();
    onSort?.(columnId, direction);
    setOpen(false);
  }, [onSort]);

  if (!sortableColumns.length || !onSort) {
    return null;
  }

  const directionLabel = sortOrder === 'asc'
    ? t('table.ascending')
    : t('table.descending');

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 0.5, mb: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SortIcon />}
          endIcon={activeColumn ? (
            sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
          ) : undefined}
          onClick={() => setOpen(true)}
          aria-label={activeColumn
            ? t('table.sortActive', { column: activeColumn.label, direction: directionLabel })
            : t('table.sort')}
          sx={{ maxWidth: '100%' }}
        >
          {activeColumn
            ? `${t('table.sort')} · ${activeColumn.label}`
            : t('table.sort')}
        </Button>
      </Box>

      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        disableSwipeToOpen
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            maxHeight: '70vh',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" component="span">{t('table.sortBy')}</Typography>
            <IconButton edge="end" onClick={() => setOpen(false)} aria-label={t('table.closeSort')}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ overflow: 'auto', py: 0 }}>
            {sortableColumns.map((column) => {
              const isActive = sortBy === column.id;
              return (
                <ListItemButton
                  key={column.id}
                  selected={isActive}
                  onClick={() => handleColumnPress(column.id)}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={column.label}
                    primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      color={isActive && sortOrder === 'asc' ? 'primary' : 'default'}
                      onClick={(event) => handleDirectionPress(column.id, 'asc', event)}
                      aria-label={t('table.sortAscending', { column: column.label })}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color={isActive && sortOrder === 'desc' ? 'primary' : 'default'}
                      onClick={(event) => handleDirectionPress(column.id, 'desc', event)}
                      aria-label={t('table.sortDescending', { column: column.label })}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
          {onResetSort && (isNonDefault || (activeColumn && defaultSortBy === '')) && (
            <>
              <Divider />
              <ListItemButton
                onClick={() => {
                  onResetSort();
                  setOpen(false);
                }}
                sx={{ py: 1.5, justifyContent: 'center' }}
              >
                <ListItemText
                  primary={t('table.resetSort')}
                  primaryTypographyProps={{ color: 'text.secondary', textAlign: 'center' }}
                />
              </ListItemButton>
            </>
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
});

MobileTableSort.displayName = 'MobileTableSort';

export default MobileTableSort;
